import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, ArrowRight, X, Truck, DollarSign } from 'lucide-react';
import { EASE } from './reveal';
import { track } from '../lib/track';

// Show a soft "welcome back — finish your quote?" card to buyers who ran
// a shipping estimate but haven't submitted a lead yet. Kept dismissible
// and session-scoped so it never nags on the same visit.

const NUDGE_DISMISS_KEY = 'nudge_dismissed';
const NUDGE_VARIANT_KEY = 'nudge_variant';
const SHOW_AFTER_MS = 8000;   // 8s dwell
const SHOW_AFTER_PX = 800;    // or 800px scroll — whichever first
const MIN_ESTIMATE_AGE_MS = 20 * 1000;  // don't badger the same visit that just estimated

// A/B copy variants — same layout, different framing. Variant tag is
// stamped on the outgoing lead as `abandoned-estimate-nudge:${id}` so
// conversion can be broken down by copy in the admin dashboard.
const VARIANTS = {
  welcome: {
    id: 'welcome',
    eyebrow: 'Welcome back',
    title: 'We saved your estimate',
    body: "Want us to lock this in with a written quote? Takes 30 seconds \u2014 we'll pre-fill the message for you.",
    cta: 'Finish my quote',
    Icon: Sparkles,
    accentText: 'text-cyan-300',
    accentBg: 'bg-cyan-500/15',
    accentBorder: 'border-cyan-400/40',
    borderGlow: 'border-cyan-400/40 shadow-[0_20px_60px_-20px_rgba(34,211,238,0.45)]',
    topLine: 'from-transparent via-cyan-400 to-transparent',
  },
  money: {
    id: 'money',
    eyebrow: "Don't leave money on the table",
    title: 'Your quote is one click away',
    body: "You already priced the delivery \u2014 lock in a written quote before the numbers move on you. We'll pre-fill everything.",
    cta: 'Claim my quote',
    Icon: DollarSign,
    accentText: 'text-emerald-300',
    accentBg: 'bg-emerald-500/15',
    accentBorder: 'border-emerald-400/40',
    borderGlow: 'border-emerald-400/40 shadow-[0_20px_60px_-20px_rgba(52,211,153,0.45)]',
    topLine: 'from-transparent via-emerald-400 to-transparent',
  },
};

function pickVariant() {
  if (typeof window === 'undefined') return 'welcome';
  try {
    const saved = window.sessionStorage.getItem(NUDGE_VARIANT_KEY);
    if (saved && VARIANTS[saved]) return saved;
    const chosen = Math.random() < 0.5 ? 'welcome' : 'money';
    window.sessionStorage.setItem(NUDGE_VARIANT_KEY, chosen);
    return chosen;
  } catch (_) {
    return 'welcome';
  }
}

function fmt(n) {
  if (n == null) return '';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const MODEL_LABEL = {
  '8x24': "8' \u00d7 24'",
  '10x30': "10' \u00d7 30' (wide load)",
};

function readEstimate() {
  try {
    const raw = window.localStorage.getItem('last_estimate');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.zip) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export default function AbandonedEstimateNudge() {
  const [visible, setVisible] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const timerRef = useRef(null);
  const dismissedRef = useRef(false);
  const contactVisibleRef = useRef(false);
  const variantId = useMemo(() => pickVariant(), []);
  const variant = VARIANTS[variantId] || VARIANTS.welcome;

  const canRender = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (window.sessionStorage.getItem(NUDGE_DISMISS_KEY) === '1') return false;
    return true;
  }, []);

  useEffect(() => {
    if (!canRender) return undefined;
    const e = readEstimate();
    if (!e) return undefined;
    // If the estimate is fresh (just ran it this visit), give the buyer
    // some breathing room before we invite them back to the quote form.
    const age = e.at ? Date.now() - new Date(e.at).getTime() : Infinity;
    if (age < MIN_ESTIMATE_AGE_MS) return undefined;
    setEstimate(e);

    // Trigger by time OR scroll — whichever fires first. Also retry
    // when the buyer scrolls the contact section back out of view, so
    // we can still nudge someone who briefly peeked at the form.
    const tryShow = () => {
      if (dismissedRef.current) return false;
      if (contactVisibleRef.current) return false;
      if (window.scrollY < SHOW_AFTER_PX) return false;
      setVisible(true);
      track('nudge_shown', { zip: e.zip, model: e.model, variant: variantId });
      window.removeEventListener('scroll', onScroll);
      return true;
    };
    // Timer path — force-show after dwell even if scrollY is low.
    timerRef.current = setTimeout(() => {
      if (dismissedRef.current || contactVisibleRef.current) return;
      setVisible(true);
      track('nudge_shown', { zip: e.zip, model: e.model, variant: variantId });
      window.removeEventListener('scroll', onScroll);
    }, SHOW_AFTER_MS);
    const onScroll = () => { tryShow(); };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Hide once the buyer reaches the quote form. When they scroll away
    // again, tryShow gets a second chance.
    const contactEl = document.getElementById('contact');
    let obs;
    if (contactEl) {
      obs = new IntersectionObserver(
        ([entry]) => {
          contactVisibleRef.current = entry.isIntersecting;
          if (entry.isIntersecting) {
            setVisible(false);
          } else {
            tryShow();
          }
        },
        { threshold: 0.05 }
      );
      obs.observe(contactEl);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('scroll', onScroll);
      if (obs) obs.disconnect();
    };
  }, [canRender]);

  const dismiss = () => {
    dismissedRef.current = true;
    setVisible(false);
    try {
      window.sessionStorage.setItem(NUDGE_DISMISS_KEY, '1');
    } catch (_) {}
    track('nudge_dismissed', { zip: estimate?.zip, model: estimate?.model, variant: variantId });
  };

  const finish = () => {
    if (!estimate) return;
    const hull = MODEL_LABEL[estimate.model] || estimate.model;
    const dest = estimate.dest_city
      ? `${estimate.dest_city}, ${estimate.dest_state} (ZIP ${estimate.zip})`
      : `ZIP ${estimate.zip}`;
    const range =
      estimate.low != null && estimate.high != null
        ? `${fmt(estimate.low)}\u2013${fmt(estimate.high)}`
        : null;
    const message =
      `Hi, I got a shipping estimate on your site for a ${hull} HDPE barge to ${dest}` +
      (range ? ` (${range})` : '') +
      `. Please send me a full quote and next steps.`;
    track('nudge_clicked', { zip: estimate.zip, model: estimate.model, variant: variantId });
    window.dispatchEvent(
      new CustomEvent('prefill-quote-message', {
        detail: { message, source: `abandoned-estimate-nudge:${variantId}` },
      })
    );
    setVisible(false);
  };

  if (!estimate) return null;
  const dest = estimate.dest_city
    ? `${estimate.dest_city}, ${estimate.dest_state}`
    : `ZIP ${estimate.zip}`;
  const range =
    estimate.low != null && estimate.high != null
      ? `${fmt(estimate.low)} \u2013 ${fmt(estimate.high)}`
      : null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="nudge"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed bottom-4 right-4 z-40 w-[calc(100%-2rem)] sm:w-[380px] max-w-[420px]"
          data-testid="abandoned-nudge"
          data-nudge-variant={variantId}
        >
          <div className={`relative rounded-xl border bg-black/90 backdrop-blur-md overflow-hidden ${variant.borderGlow}`}>
            <div className={`absolute inset-x-0 -top-px h-px bg-gradient-to-r ${variant.topLine}`} />
            <div className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${variant.accentBg} ${variant.accentBorder} ${variant.accentText}`}>
                    <variant.Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className={`text-[10px] uppercase tracking-[0.24em] font-semibold ${variant.accentText}`}>{variant.eyebrow}</div>
                    <div className="text-white text-sm font-semibold">{variant.title}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={dismiss}
                  aria-label="Dismiss"
                  className="shrink-0 text-zinc-500 hover:text-white transition-colors -mr-1 -mt-1 p-1"
                  data-testid="nudge-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="rounded-md bg-white/[0.04] border border-white/10 px-3 py-2.5 mb-3">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {range && (
                    <span className="font-display text-white text-base font-bold whitespace-nowrap">{range}</span>
                  )}
                  <span className={`text-[10px] uppercase tracking-[0.16em] ${variant.accentText}`}>
                    {MODEL_LABEL[estimate.model] || estimate.model}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-500 flex items-center gap-1.5">
                  <Truck className="w-3 h-3" />
                  to {dest}
                  {estimate.distance_miles != null ? ` \u00b7 ${estimate.distance_miles} mi` : ''}
                </div>
              </div>

              <p className="text-zinc-400 text-xs leading-relaxed mb-3.5">
                {variant.body}
              </p>

              <button
                type="button"
                onClick={finish}
                data-testid="nudge-cta"
                className="btn-teal w-full px-4 py-2.5 rounded-md text-xs uppercase tracking-[0.16em] inline-flex items-center justify-center gap-2"
              >
                {variant.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
